import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";

// The choices shown when someone cancels Premium. The words match what the server saves (backend/feedback.py).
const REASONS = [
  { key: "price", label: "Too expensive" },
  { key: "content", label: "Not enough songs or videos" },
  { key: "unused", label: "I'm not using it" },
  { key: "problem", label: "Something didn't work right" },
  { key: "other", label: "Other" },
];

// Replaces the plain "Are you sure?" pop-up. One question, and it can be skipped.
export default function CancelPremiumDialog({ open, onClose, onCanceled }) {
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setReason(""); setMessage(""); setBusy(false); } }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const send = async (withReason) => {
    setBusy(true);
    try {
      const body = withReason && (reason || message.trim())
        ? { reason: reason || "other", message: message.trim(), page: window.location.pathname }
        : undefined;
      const { data } = await api.post("/subscription/cancel", body);
      onCanceled(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel. Please try again.");
      setBusy(false);
    }
  };

  const problem = reason === "problem";
  return (
    <div data-testid="cancel-dialog" role="dialog" aria-modal="true" aria-labelledby="cancel-dialog-title"
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3" onClick={() => { if (!busy) onClose(); }}>
      <div className="w-full max-w-md max-h-[92vh] overflow-y-auto bg-zinc-900 border border-white/10 rounded-2xl p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <h2 id="cancel-dialog-title" className="font-display text-2xl font-bold">Sorry to see you go</h2>
        <p className="text-sm text-zinc-400 mt-1 mb-4">You keep Premium until the end of the month you have already paid for. One quick question, and you can skip it:</p>
        <div className="space-y-2" role="radiogroup" aria-label="Why are you canceling?">
          {REASONS.map((r) => (
            <label key={r.key} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer ${reason === r.key ? "border-cyan-400/60 bg-cyan-400/5" : "border-white/10 hover:bg-white/5"}`}>
              <input type="radio" name="cancel-reason" value={r.key} checked={reason === r.key} onChange={() => setReason(r.key)}
                data-testid={`cancel-reason-${r.key}`} className="w-4 h-4 accent-cyan-400" />
              <span className="text-sm">{r.label}</span>
            </label>
          ))}
        </div>
        {problem ? (
          <div className="mt-3 rounded-xl border border-cyan-400/40 p-3">
            <label htmlFor="cancel-message" className="block text-sm font-semibold">What happened?</label>
            <p className="text-xs text-zinc-400 mb-2">What were you doing, and what did you see? Any detail helps Dan fix it.</p>
            <textarea id="cancel-message" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} rows={3}
              placeholder="Example: I pressed play on a song and the screen went black" data-testid="cancel-message"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:border-cyan-400/50" />
            <p className="text-xs text-zinc-500 mt-2">Sent along automatically so Dan can track it down: your phone or computer, your browser, and the page you were on.</p>
          </div>
        ) : (
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} rows={2} aria-label="Anything you would like Dan to know"
            placeholder="Anything you'd like Dan to know? (optional)" data-testid="cancel-message"
            className="mt-3 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:border-cyan-400/50" />
        )}
        <p className="text-xs text-zinc-500 mt-3">Dan reads these to improve the site. They are never sold or shared for ads. <Link to="/privacy" className="text-cyan-400 hover:underline" onClick={onClose}>Privacy</Link></p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={() => send(true)} data-testid="cancel-confirm"
            className="px-5 py-2.5 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 disabled:opacity-60">{busy ? "Canceling..." : "Cancel Premium"}</button>
          <button type="button" disabled={busy} onClick={() => send(false)} data-testid="cancel-skip"
            className="px-5 py-2.5 rounded-full border border-white/20 hover:bg-white/5 disabled:opacity-60">Skip and cancel</button>
          <button type="button" disabled={busy} onClick={onClose} data-testid="cancel-keep"
            className="px-5 py-2.5 rounded-full border border-white/20 hover:bg-white/5 disabled:opacity-60">Keep Premium</button>
        </div>
      </div>
    </div>
  );
}
